import { Link, useSearchParams } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import {
  useAdminNominations, useAdminQuests, useAdminSubmissions, useQuestAdminAccess,
} from '../../hooks/useQuestAdmin';
import { CATEGORY, fmtDate } from './questShared';
import { AdminPage, BTN, Card, Loading, StateBadge } from './adminUi';
import PeopleTab from './PeopleTab';
import LeaderboardTab from './LeaderboardTab';
import PrizesTab, { lastFinishedQuarter, usePrizesToConfirm } from './PrizesTab';
import ReportsTab from './ReportsTab';
import SettingsTab from './SettingsTab';
import NominationsTab from './NominationsTab';
import SubmissionsTab from './SubmissionsTab';
import BadgeMessagesTab from './BadgeMessagesTab';
import PrizeMessagesTab from './PrizeMessagesTab';

// Nano Quest admin (brief section 9). Communications and full admins — the
// tech server refuses everyone else; this screen only says so politely.

// Five tabs, by what Comms are doing. The grouped ones have sections, picked
// with a switch at the top of the tab (?view=).
const TABS = [
  ['quests', 'Quests'],
  ['review', 'To review'],
  ['people', 'People'],
  ['results', 'Results'],
  ['settings', 'Settings'],
];
const SECTIONS = {
  review: [['submissions', 'Submissions'], ['nominations', 'Values in Action']],
  results: [['leaderboard', 'Leaderboard'], ['prizes', 'Prizes'], ['reports', 'Reports']],
  settings: [['points', 'Point values & audit'], ['messages', 'Badge messages'], ['prize-messages', 'Prize messages']],
};
// Where the old tabs went, so bookmarks and links still land in the right place.
const MOVED = {
  nominations: ['review', 'nominations'],
  submissions: ['review', 'submissions'],
  leaderboard: ['results', 'leaderboard'],
  prizes: ['results', 'prizes'],
  reports: ['results', 'reports'],
  messages: ['settings', 'messages'],
};

// What is waiting for Comms: nominations not yet decided, and submissions
// sent in or marked reviewed.
function useWaiting() {
  const nominations = useAdminNominations('pending').data?.length || 0;
  const sent = useAdminSubmissions('pending').data?.length || 0;
  const reviewed = useAdminSubmissions('reviewed').data?.length || 0;
  return { nominations, submissions: sent + reviewed, total: nominations + sent + reviewed };
}

// The switch between a tab's sections.
function SectionSwitch({ sections, value, onChange, counts = {} }) {
  return (
    <div className="mb-4 inline-flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1">
      {sections.map(([key, label]) => (
        <button key={key} type="button" onClick={() => onChange(key)}
          className={`rounded-md px-3 py-1.5 text-sm font-semibold ${
            value === key ? 'bg-white text-brand shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
          {label}
          {counts[key] ? <span className="ml-1.5 rounded-full bg-brand px-1.5 py-0.5 text-[11px] font-bold text-white">{counts[key]}</span> : null}
        </button>
      ))}
    </div>
  );
}

const num = (v, suffix = '') => (v === null || v === undefined ? '—' : `${v}${suffix}`);

function QuestsTab() {
  const q = useAdminQuests();
  const quests = q.data || [];
  return (
    <Card>
      <Loading q={q} what="the Quests" />
      {q.data && !quests.length ? (
        <p className="px-4 py-8 text-center text-sm text-gray-400">No Quests yet. Create the first one.</p>
      ) : null}
      {quests.length ? (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2">Quest</th>
                <th className="px-4 py-2">Runs</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Points</th>
                <th className="px-4 py-2 text-right">Took part</th>
                <th className="px-4 py-2 text-right">Passed</th>
                <th className="px-4 py-2 text-right">Avg score</th>
                <th className="px-4 py-2 text-right">Avg attempts</th>
                <th className="px-4 py-2 text-right">Submissions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {quests.map((x) => (
                <tr key={x.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <Link to={`/quest/quests/${x.id}`} className="font-semibold text-brand hover:text-brand-dark">
                      {x.title}
                    </Link>
                    <div className="text-xs text-gray-400">
                      {CATEGORY[x.category] || x.category} · {x.quest_type === 'task' ? 'Task' : x.quest_type === 'nominate' ? 'Values in Action — nominations' : x.quest_type === 'spot' ? `Spot the Risk, ${x.question_count} scene${x.question_count === 1 ? '' : 's'}` : `Quiz, ${x.question_count} questions`}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">{fmtDate(x.starts_at)} – {fmtDate(x.ends_at)}</td>
                  <td className="px-4 py-2.5"><StateBadge state={x.state} /></td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{x.points}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{x.participants}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {x.passed}{x.completion_rate != null ? <span className="text-gray-400"> ({x.completion_rate}%)</span> : null}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{num(x.average_score, '%')}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{num(x.average_attempts)}</td>
                  {/* nominations or challenge entries; a quiz has none */}
                  <td className="px-4 py-2.5 text-right tabular-nums">{x.submissions || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Card>
  );
}

function QuestAdmin() {
  const access = useQuestAdminAccess();
  const [params, setParams] = useSearchParams();
  const waiting = useWaiting();
  const prizesToConfirm = usePrizesToConfirm(lastFinishedQuarter());
  const asked = params.get('tab');
  const [movedTab, movedView] = MOVED[asked] || [];
  const tab = movedTab || (TABS.some(([k]) => k === asked) ? asked : 'quests');
  const sections = SECTIONS[tab];
  // the section asked for; else, on To review, whichever has something waiting
  let view = movedView || params.get('view');
  if (sections && !sections.some(([k]) => k === view)) {
    view = tab === 'review' && !waiting.submissions && waiting.nominations ? 'nominations' : sections[0][0];
  }
  const go = (t, v) => setParams(v ? { tab: t, view: v } : { tab: t }, { replace: true });

  if (access.data === false) {
    return (
      <AdminPage title="Nano Quest" back="/?dept=communications" backLabel="Back to Communications">
        <Card className="px-4 py-6 text-sm text-gray-600">
          Nano Quest admin is for the Communications team. Ask IT to add you to NEO2-Communications-Access if you need it.
        </Card>
      </AdminPage>
    );
  }

  return (
    <AdminPage
      title="Nano Quest"
      back="/?dept=communications"
      backLabel="Back to Communications"
      actions={tab === 'quests' ? (
        <Link to="/quest/quests/new" className={BTN}><PlusIcon className="h-4 w-4" /> New Quest</Link>
      ) : null}
    >
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200">
        {TABS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => go(key)}
            className={`-mb-px shrink-0 border-b-2 px-3 py-2 text-sm font-medium ${
              tab === key ? 'border-brand text-brand' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {label}
            {(key === 'review' ? waiting.total : key === 'results' ? prizesToConfirm : 0) ? (
              <span className="ml-1.5 rounded-full bg-brand px-1.5 py-0.5 text-[11px] font-bold text-white">
                {key === 'review' ? waiting.total : prizesToConfirm}
              </span>
            ) : null}
          </button>
        ))}
      </div>
      {sections ? (
        <SectionSwitch sections={sections} value={view} onChange={(v) => go(tab, v)}
          counts={tab === 'review' ? { submissions: waiting.submissions, nominations: waiting.nominations }
            : tab === 'results' ? { prizes: prizesToConfirm } : {}} />
      ) : null}
      {tab === 'quests' && <QuestsTab />}
      {tab === 'review' && view === 'submissions' && <SubmissionsTab />}
      {tab === 'review' && view === 'nominations' && <NominationsTab />}
      {tab === 'people' && <PeopleTab />}
      {tab === 'results' && view === 'leaderboard' && <LeaderboardTab />}
      {tab === 'results' && view === 'prizes' && <PrizesTab />}
      {tab === 'results' && view === 'reports' && <ReportsTab />}
      {tab === 'settings' && view === 'points' && <SettingsTab />}
      {tab === 'settings' && view === 'messages' && <BadgeMessagesTab />}
      {tab === 'settings' && view === 'prize-messages' && <PrizeMessagesTab />}
    </AdminPage>
  );
}

export default QuestAdmin;
